// @flow

import { isEmpty } from 'lodash';

import { metrics } from '@seek/metrics-js';
import HttpClient, {
  isTimeoutError as isHttpClientTimeoutError,
  isConnectionReset,
  isGatewayTimeout
} from '@seek/ca-http-client';
import authToken from '@seek/ca-http-client/dist/strategies/authToken';

import { authTokenUrl } from 'src/config';
import { findRootCause } from './error';
import { isError } from 'lodash';
import { s3Logger } from '../logger';

const httpClientMiddleware = ({ requestConfig, response = {}, duration }) => {
  const { label } = requestConfig;
  const { config = requestConfig, status } = response;
  const { method = 'get', url } = config;

  s3Logger.info(
    {
      req: requestConfig,
      res: response,
      latency: duration,
      status,
      result: status === 0 ? 'aborted' : 'success',
      label
    },
    `API response: ${label}`
  );

  metrics.httpRequestTiming({
    method,
    status,
    duration,
    isUpstream: true,
    endpointName: label || (url.indexOf('token') > -1 ? 'auth-token' : '')
  });
};

export const isTimeoutError = (error: boolean) => {
  if (!isError(error)) {
    return false;
  }

  const rootCause = findRootCause(error);
  return isHttpClientTimeoutError(rootCause);
};

const commonHttpClientConfig = {
  appName: 'Chalice',
  metrics: httpClientMiddleware,
  retryPolicy: {
    isRetriableError: err =>
      isTimeoutError(err) || isConnectionReset(err) || isGatewayTimeout(err)
  }
};

export const createAuthenticatedHttpClient = (config: Object = {}) =>
  new HttpClient({
    auth: authToken(authTokenUrl),
    ...commonHttpClientConfig,
    ...config
  });

export const createUnauthenticatedHttpClient = (config: Object = {}) =>
  new HttpClient({ ...commonHttpClientConfig, ...config });

export const withRequestId = (id: ?string) => {
  if (!id) {
    return {};
  }

  return { 'X-Request-Id': id };
};

export const withUserAgent = (userAgent: ?string) => {
  if (ENV.CLIENT || isEmpty(userAgent)) {
    return {};
  }

  return { 'X-Seek-Forwarded-User-Agent': userAgent };
};

export const localiseUrl = (locale: Locale = 'AU', auUrl: string) =>
  /nz/i.test(locale) ? auUrl.replace('.com.au', '.co.nz') : auUrl;
